import { type CSSProperties, useEffect } from 'react'

import type { Evidence, InsightBundle, TerritoryPoint } from '../lib/api'
import { OCCUPANCY_COLORS, OCCUPANCY_LABELS } from '../lib/matrixSpec'

const QUADRANT_LABELS: Record<string, string> = {
  territorio_branco: 'Território branco',
  campo_de_batalha: 'Campo de batalha',
  fortaleza: 'Fortaleza',
  descarte: 'Descarte',
}

interface Props {
  point: TerritoryPoint
  /** Todos os pontos da matriz: a escala do termômetro é relativa a eles. */
  points: TerritoryPoint[]
  bundle: InsightBundle | null
  onClose: () => void
}

export function TerritoryPanel({ point, points, bundle, onClose }: Props) {
  const actions = bundle?.playbook.filter((item) => item.territory_id === point.territory_id) ?? []
  const caveats = bundle?.reading_caveats.filter((item) => item.territory_id === point.territory_id) ?? []
  const driver = point.competitors.reduce<TerritoryPoint['competitors'][number] | null>(
    (best, candidate) => (best === null || candidate.index > best.index ? candidate : best),
    null,
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="drawer-layer">
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="Fechar detalhe" />
      <aside className="territory-drawer" role="dialog" aria-modal="true" aria-labelledby="territory-title">
        <header className="drawer-head">
          <div className="drawer-order">
            <span
              className="occupancy-dot"
              style={{ background: OCCUPANCY_COLORS[point.brand_occupancy] }}
            />
            {OCCUPANCY_LABELS[point.brand_occupancy]}
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Fechar painel">
            <span>Fechar</span><b aria-hidden="true">×</b>
          </button>
        </header>

        <div className="drawer-title">
          <p className="eyebrow">{point.folder ? `${point.folder} · ${point.pillar}` : point.pillar}</p>
          <h2 id="territory-title">{point.label}</h2>
          <p>{QUADRANT_LABELS[point.quadrant] ?? point.quadrant}</p>
        </div>

        <section className="drawer-scoreboard" aria-label="Resumo do território">
          <div><strong>{point.reach_pct.toFixed(1)}%</strong><span>alcance</span></div>
          <div><strong>{(point.adjusted_priority_score ?? point.priority_score).toFixed(2)}</strong><span>prioridade</span></div>
          <div><strong>{point.evidence.cell_unweighted ?? '—'}</strong><span>respondentes</span></div>
        </section>

        <section className="axis-stack">
          <Axis
            index="X"
            title="Afinidade"
            value={point.affinity_index}
            rank={percentile(points.map((p) => p.affinity_index), point.affinity_index)}
            evidence={point.evidence}
            note="Audiência versus população. É o eixo sustentado pela amostra mais ampla."
          />
          <Axis
            index="Y"
            title="Afinidade competitiva"
            value={point.competitive_saturation}
            rank={percentile(points.map((p) => p.competitive_saturation), point.competitive_saturation)}
            evidence={point.saturation_evidence}
            note={driver
              ? `O maior índice é de ${displayBrand(driver.brand_id)} — usa-se o concorrente mais forte, não a média.`
              : 'Nenhum concorrente tem base nesta audiência.'}
          />
          <Axis
            index="C"
            title="Ocupação da marca"
            value={point.brand_affinity_index}
            rank={percentile(points.map((p) => p.brand_affinity_index), point.brand_affinity_index)}
            evidence={point.brand_evidence}
            note="Marca foco versus audiência. É o recorte mais estreito dos três."
          />
        </section>

        {actions.length > 0 && (
          <section className="drawer-feature">
            <p className="eyebrow">Movimento recomendado</p>
            {actions.map((action) => (
              <article key={action.action}>
                <h3>{action.action}</h3>
                <p>{action.rationale}</p>
                <span>{action.kpi_link}{action.horizon ? ` · ${action.horizon}` : ''}</span>
              </article>
            ))}
          </section>
        )}

        {caveats.length > 0 && (
          <section className="drawer-caveat">
            <p className="eyebrow">Ressalva de leitura</p>
            {caveats.map((caveat) => (
              <article key={caveat.caveat}>
                <p>{caveat.caveat}</p>
                <small><strong>Se ignorada:</strong> {caveat.impact}</small>
              </article>
            ))}
          </section>
        )}

        {point.competitors.length > 0 && (
          <section className="competitor-block">
            <div className="drawer-section-title">
              <p className="eyebrow">Leitura competitiva</p>
              <span>{point.competitors.length} marcas</span>
            </div>
            <table>
              <thead><tr><th>Marca</th><th>Índice</th><th>Célula</th><th>Base</th></tr></thead>
              <tbody>
                {[...point.competitors].sort((a, b) => b.index - a.index).map((competitor) => (
                  <tr key={competitor.brand_id} className={competitor.evidence.suppressed ? 'fragile-row' : undefined}>
                    <td>{displayBrand(competitor.brand_id)}</td>
                    <td>{competitor.index.toFixed(0)}</td>
                    <td>{competitor.evidence.cell_unweighted ?? '—'}</td>
                    <td>{competitor.evidence.unweighted_base}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="table-note">Linhas em vermelho se apoiam em célula abaixo do piso de leitura.</p>
          </section>
        )}

        {point.brand_permission && (
          <section className="permission-block">
            <div><p className="eyebrow">Permissão de marca</p><strong>{point.brand_permission.score.toFixed(2)}</strong></div>
            <p>{point.brand_permission.rationale}</p>
            <small>Julgamento humano, separado do score bruto para manter a interferência visível.</small>
          </section>
        )}
      </aside>
    </div>
  )
}

function Axis({ index, title, value, rank, evidence, note }: {
  index: string
  title: string
  value: number
  rank: number
  evidence: Evidence | null
  note: string
}) {
  const fragile = !evidence || evidence.suppressed || evidence.low_base
  return (
    <article className={`axis-card${fragile ? ' axis-fragile' : ''}`}>
      <div className="axis-card-head">
        <span>{index}</span>
        <div><h3>{title}</h3>{fragile && <small>base fina</small>}</div>
        <strong>{value.toFixed(0)}</strong>
        <Thermometer rank={rank} />
      </div>
      {evidence ? (
        <dl>
          <div><dt>Base não ponderada</dt><dd>{evidence.unweighted_base}</dd></div>
          <div><dt>Célula</dt><dd>{evidence.cell_unweighted ?? '—'}</dd></div>
          <div><dt>Intervalo 95%</dt><dd>{evidence.ci_low !== null && evidence.ci_high !== null
            ? `${evidence.ci_low.toFixed(0)}–${evidence.ci_high.toFixed(0)}` : '—'}</dd></div>
          <div><dt>Significante</dt><dd>{evidence.significant === null ? '—' : evidence.significant ? 'Sim' : 'Não'}</dd></div>
        </dl>
      ) : <p className="axis-missing">Sem evidência: a marca não tem base nesta audiência.</p>}
      <p className="axis-note">{note}</p>
    </article>
  )
}

/*
 * Onde o número cai entre os territórios desta matriz, de 0 a 1. Percentil, e não posição
 * entre mínimo e máximo, porque os índices têm cauda longa: com afinidade competitiva chegando a 1.300
 * em dois pontos, a escala linear achataria todo o resto no fundo do termômetro.
 */
export function percentile(values: number[], value: number): number {
  if (values.length < 2) return 0.5
  return values.filter((v) => v < value).length / (values.length - 1)
}

function Thermometer({ rank }: { rank: number }) {
  const pct = Math.round(rank * 100)
  return (
    <span
      className="thermometer"
      style={{ '--level': `${pct}%` } as CSSProperties}
      role="img"
      aria-label={`Acima de ${pct}% dos territórios desta matriz`}
      title={`Acima de ${pct}% dos territórios desta matriz`}
    />
  )
}

function displayBrand(value: string): string {
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}
